import React from 'react'
import { css } from '@emotion/react'
import { highlightBlueBase } from '../../../constants/principles'

export default () => [
  {
    section: 'Offer',
    text: 'My Instant Cash Offer',
    items: [
      {
        text: 'What is a Kelley Blue Book® Instant Cash Offer?',
        subText: (
          <>
            <div itemProp="description">
              The Kelley Blue Book<sup>&reg;</sup> Instant Cash Offer is a real offer for a specific amount to purchase a consumer's car or apply the amount toward another car. The Offer is valid for 7 days and can be immediately redeemed during business
              hours at any Participating Dealer, pending inspection. It is based on specific elements of the consumer's car, like installed options, specific condition (such as dents and mechanical issues) and local market demand.
            </div>
          </>
        )
      },
      {
        text: 'How is my Instant Cash Offer calculated?',
        subText:
          'Your Instant Cash Offer is calculated using a proprietary tool that accounts for fluctuating market conditions and used car prices that may vary from day to day, like home or stock prices. The calculation includes multiple data sources and is based on factors that affect used car pricing such as:',
        subList: [
          {
            heading: 'Your Specific Vehicle Details',
            detail: 'Our tool factors in details that are specific to your vehicle and critical to its evaluation, including mechanical condition, options, interior and exterior, mileage, year, make and model.'
          },
          {
            heading: 'Supply and Demand',
            detail:
              "Consumers have a large role in determining a car's value. When more buyers demand a particular vehicle, and its inventory is down, the value goes up. For example, when gas prices are at a premium, fuel-efficient vehicles may be in higher demand and sell for higher prices."
          },
          {
            heading: 'Historical Trends',
            detail: 'Historically, some cars hold their value and are in higher demand than others. Similarly, sometimes transaction prices for vehicles steadily increase over time, making those cars a more sought after commodity.'
          },
          {
            heading: 'Regional Differences',
            detail:
              'Buyers may be willing to pay sellers higher prices for the same vehicle depending on regional differences. For example, buyers in rural areas may consider large trucks more valuable than buyers in a city where parking spaces are tight.'
          },
          { heading: 'Local Market Influences', detail: 'We also take into account comparable vehicle sales in your area - this information may include dealer sales data, auction prices, and private seller prices from local advertisements.' }
        ],
        id: 'HowCalculated'
      },
      {
        text: 'Am I obligated to redeem my Instant Cash Offer?',
        subText: 'No. There is no obligation to trade-in or sell your car by simply receiving an Offer. The choice is yours.',
        id: 'Obligation'
      },
      {
        text: 'How is an Instant Cash Offer different from Trade-in Value Range?',
        subText: 'The Kelley Blue Book® Instant Cash Offer is:',
        subList: [
          { detail: 'A fixed offer applied toward your next car purchase or used to sell your current car to a Participating Dealer (pending inspection)' },
          { detail: 'Valid for 7 days' },
          { detail: 'No need to negotiate because each offer is a fixed amount' },
          { detail: 'Based on specific details related to the condition and features of your car, like installed options, dents, mechanical issues, and local market demand' }
        ],
        id: 'HowDifferent1'
      },
      {
        text: 'The Kelley Blue Book® Trade-In Value is:',
        subList: [
          { detail: 'An estimated trade-in value used toward the purchase of another car only' },
          { detail: 'Updated weekly' },
          { detail: 'Applied to similar cars of the same year, make, model, style and general condition' },
          { detail: 'A useful tool for negotiating' },
          {
            detail:
              'The general value Kelley Blue Book estimates consumers can expect to negotiate this week based on the style, condition, mileage and options of the vehicle when they trade it in to a dealer. However, every dealer is different and values are not guaranteed'
          }
        ],
        id: 'HowDifferent2'
      },
      {
        text: 'How much does it cost to receive an Instant Cash Offer?',
        subText: "It's free! There is no fee to obtain or redeem the Offer.",
        id: 'HowMuch'
      },
      {
        text: 'Do I have to purchase a car with my Instant Cash Offer?',
        subText: 'No. You can walk away with a check in hand or use the Offer towards the purchase of another vehicle.',
        id: 'PurchaseRequired'
      },
      {
        text: 'How long is my Instant Cash Offer valid for?',
        subText: `Your Instant Cash Offer is valid for 7 days. After that time, you can resubmit your request, but your Offer amount may change.`,
        id: 'HowLong'
      },
      {
        text: 'How long do I have to accept my Offer after inspection?',
        subText: "You'll have 1 day after inspection to redeem your Instant Cash Offer. If your car's condition changes or you add more than 50 miles from the time of inspection, your Offer may be recalculated.",
        id: 'HowLongAccept'
      },
      {
        text: 'How long will it take to receive my Instant Cash Offer?',
        subText:
          'You will typically receive an Offer within minutes. In some cases, it may take longer, such as during periods of heavy volume. The Instant Cash Offer program is not available for all makes and models, so if your vehicle falls into one of the unavailable categories, you will not receive an Offer.',
        id: 'HowLongReceive'
      },
      {
        text: "What if the Participating Dealer I go to won't match the amount on my Instant Cash Offer?",
        subText:
          'Participating Dealers are required to honor your Instant Cash Offer, provided the information and condition you provided when creating your Offer are confirmed to be accurate by the Participating Dealer. If the information is not correct, the Participating Dealer can update the information using the same online tool you used to generate the Instant Cash Offer for your car, and the update may result in a modified Offer amount. So, the more details you enter about the condition of your car, the more accurate your Offer will be.',
        id: 'DealerWontMatch'
      },
      {
        text: 'Why do offers fall out of Trade-in Value Range?',
        subText:
          "Most offers do fall within range. If they fall out of range, reasons include, but are not limited to, market volatility, rapid changes to the market, mechanical issues, excessive damage, reported issues, vehicle modifications, modified suspension, or the vehicle's specific condition.",
        id: 'OffersOutOfRange'
      }
    ]
  },
  {
    section: 'Car',
    text: 'My Car',
    items: [
      {
        text: "My car isn't in perfect condition. Can I still get an Instant Cash Offer?",
        subText: 'Yes, if your car is not in an otherwise excluded category.',
        id: 'CarNotPerfect'
      },
      {
        text: 'What makes a car ineligible for an Offer?',
        subText: `There are a variety of reasons why cars may not be eligible. Some possible reasons include, but are not limited to, title issues (salvage, grey market, taxis or limousines), market volatility, lack of market data, unresolved recalls, low value due to age or condition, vehicle modifications, or if the vehicle has been at an auction within the last 45 days.`,
        id: 'WhatIsIneligible'
      },
      {
        text: "Can I sell my car if it's financed or leased?",
        subText:
          'Yes. The Participating Dealer will process the necessary paperwork for you to pay off your vehicle and pay any related fees. Any remaining equity is yours to cash in or use toward the purchase of another vehicle. If your car is financed, bring your lienholder information. If your car is leased, check with your leasing company to find out what paperwork they require.',
        id: 'FinancedOrLeased'
      },
      {
        text: "How will my car's condition be verified?",
        subText:
          "When you present your Instant Cash Offer to the Participating Dealer, the dealer will conduct a visual inspection of the interior and exterior, then may test drive the vehicle to confirm its mechanical condition. If the inspection report doesn't match your online assessment of your car's condition, the Offer may be raised or lowered based on the dealer's inspection report.",
        id: 'HowVerified'
      },
      {
        text: 'Where can I find the Vehicle Identification Number (VIN)?',
        subList: [
          { detail: "The VIN is on the driver's side of the dashboard by the window or on the driver's side doorjamb. It is also listed on your auto insurance card and on your state vehicle registration form." },
          { detail: 'The letters "I", "O" and "Q" never appear in a VIN.' },
          { detail: 'The VIN must be exactly 17 characters in length.' },
          { detail: 'A VIN is assigned to each car by the manufacturer when the car is built. Each car manufactured after 1980 has a unique VIN.' }
        ],
        id: 'WhereIsVIN'
      }
    ]
  },
  {
    section: 'DropOff',
    text: 'Dropping Off My Car',
    items: [
      {
        text: 'Where do I take my car to redeem my Instant Cash Offer?',
        subText: "Take your car to a Participating Dealer to verify its condition and redeem your Instant Cash Offer. We'll provide you with a list of Participating Dealers, and you're free to choose which to visit.",
        id: 'WhereRedeem'
      },
      {
        text: 'How far away will the dealer be?',
        subText: "We'll do our best to identify Participating Dealers in your metro area.",
        id: 'HowFarDealer'
      },
      {
        text: 'What information should I bring when I drop off my car?',
        subText: 'To finalize the sale, bring the following information to a Participating Dealer:',
        subList: [
          { detail: 'Your Instant Cash Offer certificate' },
          { detail: "Your vehicle title. If you don't have a title because your car is financed, bring your lienholder information. If your car is leased, check with your leasing company to find out what paperwork they require." },
          { detail: 'Your vehicle registration' },
          { detail: "A valid driver's license or other government photo ID" },
          { detail: 'All keys, keyless remotes and manuals' },
          { detail: 'Service and repair records' }
        ],
        id: 'WhatToBring'
      }
    ]
  },
  {
    section: 'Contact',
    text: 'Contact Us',
    items: [
      {
        text: 'How do I contact Kelley Blue Book® about my offer?',
        subText: (
          <>
            {'If you have more questions, please email '}
            <a
              css={css`
                text-decoration: none;
                color: ${highlightBlueBase};
                &:hover {
                  text-decoration: underline;
                }
              `}
              href="mailto:instantcashoffer@kbb.com">
              Customer Support
            </a>
            {' anytime. You can also contact us at '}
            <a
              css={css`
                text-decoration: none;
                color: ${highlightBlueBase};
                &:hover {
                  text-decoration: underline;
                }
              `}
              href="tel:866-559-5268">
              (866) 559-5268
            </a>
            {', Monday through Friday from 8 a.m. to 8 p.m. or Saturday from 9 a.m. to 6 p.m. eastern time.'}
          </>
        ),
        id: 'Contact'
      }
    ]
  }
]
